import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { OtpVerificationDialogComponent } from '../otp-verification-dialog/otp-verification-dialog.component';
import { Subject, takeUntil } from 'rxjs';
import { CompanyManagementContentService } from '../../services/company-management-content.service';
import { CamService } from 'src/app/services/camService/cam.service';
import { CryptoService } from 'src/app/services/commonService/crypto.service';
import { OtpRequestPayload } from 'src/app/models/interface/Registration/registration';
import { ToastrApiService } from 'src/app/shared/common/toastr-api.service';
import { CompanyManagementService } from 'src/app/services/dataLayer/CAM/company-management.service';
import { jwtDecode } from 'jwt-decode';
import { CiamService } from 'src/app/services/CIAM/ciam.service';
import { PsqServiceService } from 'src/app/services/commonService/psq-service.service';
import { CommonService } from 'src/app/shared/common/common.service';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
    selector: 'app-enable-company-login',
    templateUrl: './enable-company-login.component.html',
    styleUrls: ['./enable-company-login.component.scss'],
    imports: [ReactiveFormsModule, FormsModule, NgIf, RouterLink]
})
export class EnableCompanyLoginComponent implements OnInit, OnDestroy {
  ModalRef!: MatDialogRef<OtpVerificationDialogComponent>;
  private destroy$ = new Subject<void>();
  content: any;
  isFederated: boolean = false;

  private emailOtpChecksum: string | null = null;
  private mobileOtpChecksum: string | null = null;
  isSendingOtp: boolean = false;
  // Traveler context
  private userId: string = '';
  private guid: string = '';
  private phoneNumber: string = '';
  private phoneExtension: string = '';
  private isLoadingTravelerInfo: boolean = false;
  private readonly otpErrorMessage = 'You have entered incorrect OTP, try again.';

  constructor(
    private _MatDialog: MatDialog,
    private contentService: CompanyManagementContentService,
    private _CamApiService: CamService,
    private crypto: CryptoService,
    private _ToastrApiService: ToastrApiService,
    private _companyManagementService: CompanyManagementService,
    private ciamService: CiamService,
    private psqService: PsqServiceService,
    private commonService: CommonService,
  ) { }


  openModal() {
    // GTM service call
    this._companyManagementService.company_login_authentication('Step1-Verify your email address','PopUp_pageview');
    this._companyManagementService.company_login_click('Enable');
    this.sendEmailOtpAndOpenDialog();
  }

  ngOnInit() {
    this.contentService.content$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      this.content = res;
    });

    const token = localStorage.getItem('QlAccessToken');
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        this.isFederated = decoded?.IsFederatedAccount === 'True' || decoded?.isFederatedAccount === 'True';
      } catch (e) {
        console.error('Error decoding token in EnableCompanyLoginComponent', e);
      }
    }

    // Load traveler identifiers for mobile OTP step
    const tokenClaims = this.getQlAccessTokenClaims();
    this.userId =sessionStorage.getItem('userId') ||(this.crypto.getValueFromKey('USERID') as string) ||tokenClaims.userId ||'';
    this.guid =sessionStorage.getItem('guid') ||(this.crypto.getValueFromKey('USERGUID') as string) ||tokenClaims.userGuid ||'';
    this.loadTravelerInfoForOtp();
  }

  getFederatedDescriptionParts(): { part1: string; part2: string } {
    const text = this.content?.['b2b.managecompany.omffedaratelogindesc'] || '';
    const searchTerms = ['help & support.', 'help \u0026 support.', 'help & support', 'help \u0026 support'];
    for (const term of searchTerms) {
      const index = text.indexOf(term);
      if (index !== -1) {
        return {
          part1: text.substring(0, index),
          part2: term
        };
      }
    }
    return {
      part1: text,
      part2: ''
    };
  }

  private async loadTravelerInfoForOtp(): Promise<void> {
    if (!this.userId || !this.guid || this.isLoadingTravelerInfo) return;

    try {
      this.isLoadingTravelerInfo = true;
      const payload = {
        subagentId: this.crypto.getValueFromKey('SUBAGENTID') as string,
        userRole: this.crypto.getValueFromKey('ISCORPTRAVELARRANGER') as string,
        B2BUserId: this.userId,
      };
      const response: any = await this._CamApiService.PostUserExecutiveV1(this.guid, payload);

      if (response?.status === 'Success') {
        const personalInfo = response?.data?.personalInfo || {};
        this.phoneNumber = personalInfo?.phoneNumber || '';
        this.phoneExtension = personalInfo?.phoneExtension || '';
      }
    } catch (error: any) {
      const message =
        error?.error?.message ||
        error?.message ||
        'Something went wrong. Please try again.';
      this._companyManagementService.view_error_message(message, error?.code);
      console.error('Error loading traveler info for OTP:', error);
    } finally {
      this.isLoadingTravelerInfo = false;
    }
  }

  private getLoginEmail(): string {
    return (this.crypto.getValueFromKey('LOGINNAME') as string | null)?.toLowerCase() || '';
  }

  private buildEmailSendOtpPayload(): OtpRequestPayload {
    const loginEmail = this.getLoginEmail();
    return {
      type: 'email',
      otpAction: 'mobileOrEmailValidation',
      value: loginEmail,
      channel: 'email',
      companyId: 'SAA',
      b2BId: null,
      b2BUserId: null,
      eventName: 'EnableCompanyLogin',
    };
  }

  /** Must match verify payload `value` for mobile OTP. */
  private normalizedMobileForOtp(): string {
    return (this.phoneNumber || '').replace(/[^+\d]/g, '');
  }

  private buildMobileSendOtpPayload(): OtpRequestPayload {
    return {
      type: 'mobile',
      otpAction: 'mobileOrEmailValidation',
      value: this.phoneExtension + this.normalizedMobileForOtp(),
      channel: 'mobile',
      companyId: 'SAA',
      b2BId: null,
      b2BUserId: null,
      eventName: 'EnableCompanyLogin',
    };
  }

  private getQlAccessTokenClaims(): { accountId: string; companyName: string; userId: string; userGuid: string; ciamId: string } {
    const token = localStorage.getItem('QlAccessToken');
    if (!token) {
      return { accountId: '', companyName: '', userId: '', userGuid: '', ciamId: '' };
    }

    try {
      const decoded: any = jwtDecode(token);

      return {
        accountId: decoded?.AccountId || '',
        companyName: decoded?.companyName || '',
        userId: decoded?.userId || '',
        userGuid: decoded?.userGuid || '',
        ciamId: decoded?.ciamId || '',
      };
    } catch {
      return { accountId: '', companyName: '', userId: '', userGuid: '', ciamId: '' };
    }
  }

  private buildSsoTicketPayload() {
    const { accountId, companyName, ciamId } = this.getQlAccessTokenClaims();

    if (!accountId || !companyName) {
      throw new Error('Unable to create SSO ticket. Missing account details.');
    }

    const nameWithoutSpaces = companyName.replace(/\s+/g, '');

    return {
      connection_config: {
        name: nameWithoutSpaces,
      },
      enabled_clients: [],
      enabled_organizations: [
        {
          ciam_user_id: ciamId,
          assign_membership_on_login: true,
        },
      ],
      ttl_sec: 420000,
      domain_aliases_config: {
        domain_verification: 'required',
      },
    };
  }

  private extractSsoRedirectUrl(ssoResponse: any): string | null {
    if (!ssoResponse) return null;
    const data = ssoResponse.data ?? ssoResponse;
    const raw =data?.ticket ?? ssoResponse?.ticket ?? ssoResponse?.body?.ticket;
    if (raw == null || typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  }

  private async requestSsoTicketAndRedirect(): Promise<void> {
    const ssoResponse: any = await this._CamApiService.createSsoTicket(this.buildSsoTicketPayload());
    const ticketUrl = this.extractSsoRedirectUrl(ssoResponse);

    if (ticketUrl) {
      window.open(ticketUrl, '_blank', 'noopener,noreferrer');
      this.psqService.clearStoredData();
      this.commonService.triggerLogoutTab();
      this.ciamService.logout();
      return;
    }

    throw new Error('Unable to create SSO ticket. Please try again.');
  }

  private async sendEmailOtp(): Promise<string> {
    return this.sendOtpByType('email');
  }

  private async sendMobileOtp(): Promise<string> {
    return this.sendOtpByType('mobile');
  }

  private async sendOtpByType(type: 'email' | 'mobile'): Promise<string> {
    const payload = type === 'email'
      ? this.buildEmailSendOtpPayload()
      : this.buildMobileSendOtpPayload();
    const res: any = await this._CamApiService.sendOTP(payload);
    const checksum = res?.data?.checksum as string | undefined;
    if (!checksum) throw new Error('OTP checksum missing from response');
    if (type === 'email') {
      this.emailOtpChecksum = checksum;
    } else {
      this.mobileOtpChecksum = checksum;
    }
    return checksum;
  }

  // Called by email dialog for resend
  async resendEmailOtp(): Promise<{ success: boolean; message: string; checksum?: string }> {
    try {
      const checksum = await this.sendEmailOtp();
      const message = 'OTP sent successfully.';
      this._ToastrApiService.show('success', message);
      return { success: true, message, checksum };
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to resend OTP. Please try again.';
      this._companyManagementService.view_error_message(message,error?.code);
      this._ToastrApiService.show('error', message);
      throw { success: false, message };
    }
  }

  // Called by mobile dialog for resend
  async resendMobileOtp(): Promise<{ success: boolean; message: string; checksum?: string }> {
    try {
      const checksum = await this.sendMobileOtp();
      const message = 'OTP sent successfully.';
      this._ToastrApiService.show('success', message);
      return { success: true, message, checksum };
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to resend OTP. Please try again.';
      this._companyManagementService.view_error_message(message,error?.code);
      this._ToastrApiService.show('error', message);
      throw { success: false, message };
    }
  }

  // Called by email dialog for verify
  async verifyEmailOtp(otp: string, checksum?: string): Promise<{ success: boolean; message: string }> {
    return this.verifyOtpByType('email', otp, checksum);
  }

  /**
   * After mobile OTP succeeds, SSO runs in the same step. If SSO fails, return postVerifyFailed
   * so the dialog does not treat it as a wrong OTP.
   */
  async verifyMobileOtp(
    otp: string,
    checksum?: string
  ): Promise<{ success: boolean; message: string; postVerifyFailed?: boolean }> {
    const result = await this.verifyOtpByType('mobile', otp, checksum);
    if (!result.success) {
      return result;
    }
    try {
      const { companyName } = this.getQlAccessTokenClaims();
      const nameWithoutSpaces = companyName ? companyName.replace(/\s+/g, '') : '';

      if (nameWithoutSpaces) {
        const fedDetails: any = await this._CamApiService.getFederatedConnectionDetails(nameWithoutSpaces);
        const data = fedDetails?.data ?? [];
        if (Array.isArray(data) && data.length > 0) {
          const id = data[0]?.id;
          if (id) {
            await this._CamApiService.deleteFederatedConnection(id);
          }
        }
      }

      await this.requestSsoTicketAndRedirect();
      return result;
    } catch (e: any) {
      const message =
        e?.error?.message ||
        e?.message ||
        'Unable to complete company login. Please try again.';
      this._companyManagementService.view_error_message(message, e?.code);
      this._ToastrApiService.show('error', message);
      return { success: false, message, postVerifyFailed: true };
    }
  }

  private async verifyOtpByType(
    type: 'email' | 'mobile',
    otp: string,
    checksum?: string
  ): Promise<{ success: boolean; message: string }> {
    const resolvedChecksum =
      checksum ||
      (type === 'email' ? this.emailOtpChecksum : this.mobileOtpChecksum);
    const verificationPayload = {
      otp,
      checksum: resolvedChecksum,
      type,
      value: type === 'email' ? this.getLoginEmail() : this.phoneExtension + this.normalizedMobileForOtp(),
      otpAction: 'mobileOrEmailValidation',
      channel: type,
      companyId: 'SAA',
      eventName: 'EnableCompanyLogin',
      b2BId: null,
      b2BUserId: null,
    };

    if (!verificationPayload.checksum) {
      const message = 'OTP checksum missing. Please resend OTP.';
      this._ToastrApiService.show('error', message);
      return { success: false, message };
    }

    try {
      const res: any = await this._CamApiService.VerifyOTP(verificationPayload);
      const otpVerified = res?.data?.otpVerified;

      if (otpVerified === true) {
        const message = 'OTP verified successfully.';
        this._ToastrApiService.show('success', message);
        return { success: true, message };
      }

      const message = res?.data?.message || res?.message || this.otpErrorMessage;
      this._companyManagementService.view_error_message(message, res?.code);
      this._ToastrApiService.show('error', message);
      return { success: false, message };
    } catch (error: any) {
      const message =
        error?.error?.message ||
        error?.error?.error?.message ||
        error?.message ||
        'OTP verification failed. Please try again.';
      this._companyManagementService.view_error_message(message, error?.code);
      this._ToastrApiService.show('error', message);
      return { success: false, message };
    }
  }

  // Step 1: Email OTP
  private async sendEmailOtpAndOpenDialog() {
    if (this.isSendingOtp) return;
    const loginEmail = this.getLoginEmail();

    if (!loginEmail) {
      this._ToastrApiService.show('error', 'Unable to send OTP. Login email not found.');
      return;
    }

    await this.openOtpDialogFlow('email', loginEmail);
  }

  // Step 2: Mobile OTP
  private async sendMobileOtpAndOpenDialog() {
    if (!this.phoneNumber) {
      await this.loadTravelerInfoForOtp();
    }

    if (!this.phoneNumber) {
      this._ToastrApiService.show('error', 'Unable to send OTP. Mobile number not found.');
      return;
    }

    await this.openOtpDialogFlow('mobile');
  }

  private async openOtpDialogFlow(type: 'email' | 'mobile', loginEmail?: string): Promise<void> {
    if (this.isSendingOtp) return;

    try {
      this.isSendingOtp = true;
      const checksum = await this.sendOtpByType(type);
      const dialogData: any = {
        verifyMode: type,
        step: type === 'email' ? 1 : 2,
        totalSteps: 2,
        otpChecksum: checksum,
        onResend: () => (type === 'email' ? this.resendEmailOtp() : this.resendMobileOtp()),
        onVerify: (otpValue: string, otpChecksum: string) =>
          type === 'email'
            ? this.verifyEmailOtp(otpValue, otpChecksum)
            : this.verifyMobileOtp(otpValue, otpChecksum),
      };

      if (type === 'email') {
        dialogData.Email = loginEmail || this.getLoginEmail();
      } else {
        dialogData.PhoneNumber = this.phoneNumber;
        dialogData.isdCode = this.phoneExtension;
      }

      this.ModalRef = this._MatDialog.open(OtpVerificationDialogComponent, {
        panelClass: 'custom-popup-gen-link-account-popup',
        data: {
          ...dialogData,
          EnableCompanyLogin: 'CompanyLogin'
        }
      });

      this.ModalRef.afterClosed().subscribe((result: any) => {
        if (type === 'email' && result?.verified) {
          this.sendMobileOtpAndOpenDialog();
        }
      });
    } catch (err: any) {
      const message =
        err?.error?.message ||
        err?.message ||
        'Failed to send OTP. Please try again.';
      this._companyManagementService.view_error_message(message, err?.code);
      this._ToastrApiService.show('error', message);
    } finally {
      this.isSendingOtp = false;
    }
  }

  // ModalRef!: MatDialogRef<LinkAccountDialogComponent>;
  // constructor(private _MatDialog: MatDialog) { }


  // openModal() {
  //   this.ModalRef = this._MatDialog.open(LinkAccountDialogComponent, { panelClass: 'custom-popup-gen-link-account-popup' });
  // }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
